import {
  buildOrgServiceInstanceListPath,
  buildServiceVariantHomePath,
  SERVICE_INSTANCE_PARAM,
  withServiceInstanceId,
} from '@/lib/service-instance'
import { isOrganizationServiceVariant } from '@/lib/service-variant-types'

const AUTH_REDIRECT_CONTEXT_KEY = 'auth-redirect-context'

export type AuthRedirectReason = 'session-expired'

type AuthRedirectContext = {
  from?: string
  reason?: AuthRedirectReason
}

type RedirectPostContext = {
  service_variant_unique_id?: string | number | null
  service_variant_name?: string | null
  service_variant_type?: string | null
}

const SERVICE_WORKSPACE_PREFIXES = [
  '/job',
  '/jobs',
  '/workers',
  '/vehicles',
  '/locations',
  '/assignments',
  '/workdays',
  '/timeline',
  '/planner',
  '/reports',
  '/finance',
]

const ORG_ONLY_PREFIXES = [
  '/org',
  '/org/node',
  '/org/tree',
]

function isOrganizationScopePost(post?: RedirectPostContext | null) {
  return isOrganizationServiceVariant(
    post?.service_variant_unique_id,
    post?.service_variant_name ?? post?.service_variant_type,
  )
}

function isServiceWorkspacePath(pathname: string) {
  return SERVICE_WORKSPACE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

function isOrganizationOnlyPath(pathname: string) {
  return ORG_ONLY_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function persistAuthRedirectContext(context: AuthRedirectContext) {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.setItem(AUTH_REDIRECT_CONTEXT_KEY, JSON.stringify(context))
}

export function consumeAuthRedirectContext(): AuthRedirectContext | null {
  if (typeof window === 'undefined') {
    return null
  }

  const rawValue = window.sessionStorage.getItem(AUTH_REDIRECT_CONTEXT_KEY)
  if (!rawValue) {
    return null
  }

  window.sessionStorage.removeItem(AUTH_REDIRECT_CONTEXT_KEY)

  try {
    return JSON.parse(rawValue) as AuthRedirectContext
  } catch {
    return null
  }
}

export function buildLoginRedirectPath(
  serviceInstanceId?: string | null,
  reason?: AuthRedirectReason,
  from?: string | null,
) {
  const params = new URLSearchParams()

  if (reason) {
    params.set('reason', reason)
  }

  if (from) {
    params.set('from', from)
  }

  if (serviceInstanceId) {
    params.set(SERVICE_INSTANCE_PARAM, serviceInstanceId)
  }

  const query = params.toString()
  return query ? `/login?${query}` : '/login'
}

export function buildPostSelectionPath(
  serviceInstanceId?: string | null,
  from?: string | null,
) {
  const params = new URLSearchParams()

  if (from) {
    params.set('from', from)
  }

  if (serviceInstanceId) {
    params.set(SERVICE_INSTANCE_PARAM, serviceInstanceId)
  }

  const query = params.toString()
  return query ? `/posts?${query}` : '/posts'
}

export function getSafeAuthRedirectTarget(from?: string | null, serviceInstanceId?: string | null) {
  if (!from) {
    return null
  }

  try {
    const url = new URL(from, 'http://localhost')
    const resolvedServiceInstanceId = url.searchParams.get(SERVICE_INSTANCE_PARAM) || serviceInstanceId || null
    const nodeId = url.searchParams.get('nodeId')
    const pathSegments = url.pathname.split('/').filter(Boolean)
    const trailingSegment = pathSegments[pathSegments.length - 1]

    const isLegacyConfigForm =
      url.pathname.startsWith('/org/node/service-instrance/config-form/')
      || url.pathname.startsWith('/organization/node/service-instrance/config-form/')
    const isCanonicalFormConfigurationTab =
      (url.pathname.startsWith('/org/service-instance/form/')
        || url.pathname.startsWith('/organization/service-instance/form/'))
      && url.searchParams.get('tab') === 'configuration'

    if ((isLegacyConfigForm || isCanonicalFormConfigurationTab) && nodeId) {
      return withServiceInstanceId(buildOrgServiceInstanceListPath(nodeId), resolvedServiceInstanceId)
    }

    if (url.pathname.startsWith('/org/node/service-instrance/media-form/') && nodeId) {
      return withServiceInstanceId(buildOrgServiceInstanceListPath(nodeId), resolvedServiceInstanceId)
    }

    if (url.pathname.startsWith('/org/node/branch-instrance/media-form/') && nodeId) {
      return withServiceInstanceId(buildOrgServiceInstanceListPath(nodeId), resolvedServiceInstanceId)
    }

    if (
      (url.pathname.startsWith('/org/node/service-instrance/form/')
        || url.pathname.startsWith('/org/service-instrance/form/')
        || url.pathname.startsWith('/org/service-instance/form/')
        || url.pathname.startsWith('/organization/node/service-instrance/form/')
        || url.pathname.startsWith('/organization/service-instrance/form/')
        || url.pathname.startsWith('/organization/service-instance/form/')
        || url.pathname.startsWith('/organization/node/service-instance/form/'))
      && trailingSegment
    ) {
      return withServiceInstanceId(buildOrgServiceInstanceListPath(trailingSegment), resolvedServiceInstanceId)
    }

    if (url.pathname.startsWith('/org/node/client-instrance/form/') && trailingSegment) {
      return withServiceInstanceId(`/org/node/client-add/${trailingSegment}`, resolvedServiceInstanceId)
    }

    return from
  } catch {
    return from
  }
}

export function getPostSelectionRedirectTarget(
  post?: RedirectPostContext | null,
  from?: string | null,
  serviceInstanceId?: string | null,
) {
  const safeTarget = getSafeAuthRedirectTarget(from, serviceInstanceId)
  const isOrganizationPost = isOrganizationScopePost(post)
  const dashboardTarget = buildServiceVariantHomePath(post, serviceInstanceId)

  if (!safeTarget) {
    return dashboardTarget
  }

  try {
    const url = new URL(safeTarget, 'http://localhost')
    const targetServiceInstanceId = url.searchParams.get(SERVICE_INSTANCE_PARAM)

    if (targetServiceInstanceId && serviceInstanceId && targetServiceInstanceId !== serviceInstanceId) {
      return dashboardTarget
    }

    if (isOrganizationPost && isServiceWorkspacePath(url.pathname)) {
      return dashboardTarget
    }

    if (!isOrganizationPost && isOrganizationOnlyPath(url.pathname)) {
      return dashboardTarget
    }

    return safeTarget
  } catch {
    return dashboardTarget
  }
}